/**
 * AutoGlow · Advisor · Visualization components.
 * FunnelChart · TrendChart · HeatMap · LeaderboardTable · CalendarView · RankedList.
 *
 * 100% SVG puro · cero dependencias externas (Sprint T-13 · regla del Founder).
 * All pure visual rendering · NO calculations · NO business logic.
 */
import React from "react";
import { StatusBadge } from "./Badges";

// ---------------------------------------------------------------------------
// FunnelChart · 5-stage AARRR funnel from AG-02
// ---------------------------------------------------------------------------

const PILLAR_LABEL = {
  acquisition: "Acquisition",
  activation: "Activation",
  retention: "Retention",
  revenue: "Revenue",
  referral: "Referral",
};

export function FunnelChart({ stages, global, weakest, strongest, testId }) {
  if (!stages) {
    return (
      <div className="text-sm text-[var(--ag-text-dim)]">Información insuficiente.</div>
    );
  }
  const order = ["acquisition", "activation", "retention", "revenue", "referral"];
  return (
    <div data-testid={testId || "funnel-chart"} className="space-y-2">
      {order.map((p) => {
        const st = stages[p];
        if (!st) return null;
        const score = st.score ?? 0;
        const isWeakest = weakest === p;
        const isStrongest = strongest === p;
        return (
          <div
            key={p}
            data-testid={`funnel-stage-${p}`}
            className="flex items-center gap-2"
          >
            <div className="w-24 text-xs text-[var(--ag-text)]">{PILLAR_LABEL[p]}</div>
            <div className="relative h-3 flex-1 overflow-hidden rounded bg-[var(--ag-surface-2)]">
              <div
                className={`absolute inset-y-0 left-0 rounded ${
                  isWeakest ? "bg-red-500/60" : isStrongest ? "bg-emerald-500/70" : "bg-blue-500/60"
                }`}
                style={{ width: `${Math.min(Math.max(score, 0), 100)}%` }}
                aria-label={`${p}: ${score}/100`}
              />
            </div>
            <div className="w-10 text-right text-xs font-mono">{score}</div>
            <StatusBadge status={st.label} testId={`funnel-label-${p}`} />
          </div>
        );
      })}
      {global != null && (
        <div className="mt-3 flex items-center justify-between rounded border border-[var(--ag-border)] bg-[var(--ag-surface-2)] px-3 py-2">
          <span className="text-xs font-medium text-[var(--ag-text)]">Funnel Health Global</span>
          <span className="text-xl font-bold">{global}</span>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// TrendChart · SVG-only sparkline · NO recharts.
// Series shape: [{ date: "YYYY-MM-DD", count: number }]
// ---------------------------------------------------------------------------

export function TrendChart({ series, testId, height = 160, accentColor = "#3b82f6" }) {
  if (!series || series.length === 0) {
    return (
      <div className="text-sm text-[var(--ag-text-dim)]">Información insuficiente.</div>
    );
  }
  const w = 600;
  const h = height;
  const pad = { top: 8, right: 8, bottom: 24, left: 24 };
  const counts = series.map((p) => Number(p.count) || 0);
  const maxV = Math.max(1, ...counts);
  const minV = 0;
  const xs = (i) => pad.left + (i * (w - pad.left - pad.right)) / Math.max(1, series.length - 1);
  const ys = (v) => h - pad.bottom - ((v - minV) / (maxV - minV)) * (h - pad.top - pad.bottom);

  const path = series.map((p, i) => `${i === 0 ? "M" : "L"}${xs(i)},${ys(p.count)}`).join(" ");
  const area = `${path} L${xs(series.length - 1)},${h - pad.bottom} L${xs(0)},${h - pad.bottom} Z`;

  // Few tick labels · first/middle/last
  const tickIdxs = series.length > 4 ? [0, Math.floor(series.length / 2), series.length - 1] : series.map((_, i) => i);

  return (
    <div data-testid={testId || "trend-chart"} className="w-full overflow-hidden">
      <svg viewBox={`0 0 ${w} ${h}`} className="h-auto w-full" role="img" aria-label="serie temporal">
        <path d={area} fill={accentColor} fillOpacity="0.12" />
        <path d={path} fill="none" stroke={accentColor} strokeWidth="2" />
        {/* Baseline */}
        <line x1={pad.left} y1={h - pad.bottom} x2={w - pad.right} y2={h - pad.bottom} stroke="var(--ag-border)" />
        {/* Y axis ticks (min/max) */}
        <text x={4} y={ys(maxV) + 4} fill="var(--ag-text-dim)" fontSize="10">{maxV}</text>
        <text x={4} y={h - pad.bottom + 4} fill="var(--ag-text-dim)" fontSize="10">0</text>
        {/* X axis ticks */}
        {tickIdxs.map((i) => (
          <text key={i} x={xs(i)} y={h - 6} fill="var(--ag-text-dim)" fontSize="10" textAnchor="middle">
            {(series[i].date || "").slice(5)}
          </text>
        ))}
      </svg>
    </div>
  );
}

// ---------------------------------------------------------------------------
// HeatMap · code × city grid for demand/supply (founder)
// ---------------------------------------------------------------------------

const BAND_FILL = {
  "+++": "bg-red-500/70 text-white",
  "++": "bg-orange-500/70 text-white",
  "+": "bg-yellow-500/60 text-black",
  "0": "bg-[var(--ag-surface-2)] text-[var(--ag-text-dim)]",
};

export function HeatMap({ cells, testId, maxCodes = 20, maxCities = 12 }) {
  if (!cells || cells.length === 0) {
    return (
      <div className="text-sm text-[var(--ag-text-dim)]">Información insuficiente.</div>
    );
  }
  // Build matrix: codes × cities
  const codes = Array.from(new Set(cells.map((c) => c.code))).sort().slice(0, maxCodes);
  const cities = Array.from(new Set(cells.map((c) => c.city))).sort().slice(0, maxCities);
  const map = {};
  for (const c of cells) map[`${c.code}|${c.city}`] = c;

  return (
    <div
      data-testid={testId || "heatmap"}
      className="overflow-x-auto"
    >
      <table className="min-w-full border-collapse text-xs">
        <thead>
          <tr>
            <th className="sticky left-0 z-10 bg-[var(--ag-surface)] p-1.5 text-left font-medium text-[var(--ag-text-dim)]">
              Código
            </th>
            {cities.map((city) => (
              <th key={city} className="p-1.5 text-center font-medium text-[var(--ag-text-dim)]">
                {city}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {codes.map((code) => (
            <tr key={code}>
              <td className="sticky left-0 z-10 bg-[var(--ag-surface)] p-1.5 font-mono text-[var(--ag-text)]">
                {code}
              </td>
              {cities.map((city) => {
                const cell = map[`${code}|${city}`];
                const tone = cell ? BAND_FILL[cell.gap_label] : BAND_FILL["0"];
                return (
                  <td
                    key={`${code}-${city}`}
                    data-testid={`heatmap-cell-${code}-${city}`}
                    className={`p-1.5 text-center ${tone}`}
                    title={cell ? `${cell.demand_signal}→${cell.supply_signal}` : "sin gap"}
                  >
                    {cell ? cell.gap_label : ""}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ---------------------------------------------------------------------------
// LeaderboardTable · top performers (founder-only)
// ---------------------------------------------------------------------------

export function LeaderboardTable({ leaders, metric, testId }) {
  if (!leaders || leaders.length === 0) {
    return (
      <div className="text-sm text-[var(--ag-text-dim)]">Información insuficiente.</div>
    );
  }
  return (
    <div data-testid={testId || "leaderboard-table"} className="overflow-x-auto">
      <table className="min-w-full text-xs">
        <thead>
          <tr className="border-b border-[var(--ag-border)] text-[var(--ag-text-dim)]">
            <th className="px-2 py-1.5 text-left">Rank</th>
            <th className="px-2 py-1.5 text-left">Centro</th>
            <th className="px-2 py-1.5 text-left">Ciudad</th>
            <th className="px-2 py-1.5 text-right">{metric}</th>
            <th className="px-2 py-1.5 text-right">Funnel</th>
            <th className="px-2 py-1.5 text-right">Business</th>
          </tr>
        </thead>
        <tbody>
          {leaders.map((l) => (
            <tr key={`${l.org_id}-${l.rank}`} className="border-b border-[var(--ag-border)]/40">
              <td className="px-2 py-1.5 font-mono">{l.rank}</td>
              <td className="px-2 py-1.5">{l.name || "—"}</td>
              <td className="px-2 py-1.5 text-[var(--ag-text-dim)]">{l.city || "—"}</td>
              <td className="px-2 py-1.5 text-right font-mono">{l.value ?? "—"}</td>
              <td className="px-2 py-1.5 text-right font-mono text-[var(--ag-text-dim)]">{l.funnel_health ?? "—"}</td>
              <td className="px-2 py-1.5 text-right font-mono text-[var(--ag-text-dim)]">{l.business_score ?? "—"}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ---------------------------------------------------------------------------
// CalendarView · weekly/monthly plan from AG-02
// ---------------------------------------------------------------------------

export function CalendarView({ calendar, testId }) {
  if (!calendar) {
    return (
      <div className="text-sm text-[var(--ag-text-dim)]">Información insuficiente.</div>
    );
  }
  const slots = Object.entries(calendar);
  return (
    <div data-testid={testId || "calendar-view"} className="space-y-3">
      {slots.map(([slotKey, items]) => (
        <div key={slotKey} data-testid={`calendar-slot-${slotKey}`}>
          <div className="mb-1 flex items-center gap-2">
            <span className="text-xs font-semibold uppercase tracking-wide text-[var(--ag-text-dim)]">
              {slotKey.replace("_", " ")}
            </span>
            <span className="text-[10px] text-[var(--ag-text-dim)]">
              ({(items || []).length} acciones)
            </span>
          </div>
          {items && items.length > 0 ? (
            <ul className="space-y-1">
              {items.map((it) => (
                <li
                  key={it.experiment_id || it.play_id}
                  className="rounded border border-[var(--ag-border)] bg-[var(--ag-surface)] px-2 py-1.5 text-xs"
                >
                  <span className="mr-2 font-mono text-[var(--ag-text-dim)]">{it.play_id}</span>
                  <span>{it.title}</span>
                  {it.estimated_effort_h != null && (
                    <span className="ml-2 text-[var(--ag-text-dim)]">
                      ({it.estimated_effort_h}h)
                    </span>
                  )}
                </li>
              ))}
            </ul>
          ) : (
            <div className="rounded border border-dashed border-[var(--ag-border)] p-2 text-xs text-[var(--ag-text-dim)]">
              Sin acciones programadas.
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// RankedList · generic ordered list with optional sub-citations
// ---------------------------------------------------------------------------

export function RankedList({ items, renderRight, emptyMessage, testId }) {
  if (!items || items.length === 0) {
    return (
      <div className="text-xs text-[var(--ag-text-dim)]">
        {emptyMessage || "Información insuficiente."}
      </div>
    );
  }
  return (
    <ul data-testid={testId || "ranked-list"} className="space-y-1">
      {items.map((it, i) => (
        <li
          key={it.id || it.code || it.label || i}
          className="flex items-center justify-between rounded border border-[var(--ag-border)] bg-[var(--ag-surface)] px-2 py-1.5"
        >
          <div className="flex items-center gap-2 text-xs">
            <span className="font-mono text-[var(--ag-text-dim)]">{i + 1}</span>
            <span className="text-[var(--ag-text)]">
              {it.code || it.label || it.name || it.city}
            </span>
            {it.label && it.code && (
              <span className="text-[var(--ag-text-dim)]">· {it.label}</span>
            )}
          </div>
          <div className="text-xs font-mono text-[var(--ag-text-dim)]">
            {renderRight ? renderRight(it) : it.value ?? it.orgs ?? it.count ?? ""}
          </div>
        </li>
      ))}
    </ul>
  );
}

// ---------------------------------------------------------------------------
// SectorBars · for client view of sector aggregates (anonymized)
// Renders deciles (p25/p50/p75/p90) + optional "Tú" highlight · pure SVG.
// ---------------------------------------------------------------------------

export function SectorBars({ deciles, your_value, testId }) {
  if (!deciles) {
    return (
      <div className="text-sm text-[var(--ag-text-dim)]">Información insuficiente.</div>
    );
  }
  const rows = [
    { label: "p25", value: deciles.funnel_p25 ?? deciles.business_p25 },
    { label: "p50", value: deciles.funnel_p50 ?? deciles.business_p50 },
    { label: "p75", value: deciles.funnel_p75 ?? deciles.business_p75 },
    { label: "p90", value: deciles.funnel_p90 ?? deciles.business_p90 },
    ...(your_value != null ? [{ label: "Tú", value: your_value, isYou: true }] : []),
  ].filter((r) => r.value != null);

  if (rows.length === 0) {
    return (
      <div className="text-sm text-[var(--ag-text-dim)]">Información insuficiente.</div>
    );
  }

  const maxV = Math.max(100, ...rows.map((r) => Number(r.value) || 0));

  return (
    <div data-testid={testId || "sector-bars"} className="space-y-1.5">
      {rows.map((r) => {
        const pct = Math.min(100, (Number(r.value) / maxV) * 100);
        return (
          <div key={r.label} className="flex items-center gap-2">
            <span className={`w-10 text-xs ${r.isYou ? "font-semibold text-emerald-500" : "text-[var(--ag-text-dim)]"}`}>
              {r.label}
            </span>
            <div className="relative h-2 flex-1 overflow-hidden rounded bg-[var(--ag-surface-2)]">
              <div
                className={`absolute inset-y-0 left-0 rounded ${r.isYou ? "bg-emerald-500/70" : "bg-blue-500/50"}`}
                style={{ width: `${pct}%` }}
              />
            </div>
            <span className="w-10 text-right font-mono text-xs">{r.value}</span>
          </div>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// BreadthGauge · big horizontal coverage bar (founder)
// ---------------------------------------------------------------------------

export function BreadthGauge({ covered, total, label, testId }) {
  if (covered == null || !total) {
    return <div className="text-xs text-[var(--ag-text-dim)]">Información insuficiente.</div>;
  }
  const pct = Math.min(100, Math.round((covered / total) * 100 * 100) / 100);
  return (
    <div data-testid={testId || "breadth-gauge"} className="space-y-1">
      <div className="flex items-baseline justify-between">
        <span className="text-xs text-[var(--ag-text-dim)]">{label || "Cobertura taxonómica"}</span>
        <span className="text-xs font-mono">{covered}/{total} · {pct}%</span>
      </div>
      <div className="h-3 w-full overflow-hidden rounded bg-[var(--ag-surface-2)]">
        <div className="h-3 rounded bg-blue-500/70" style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}
